/* eslint-disable camelcase */
import React, { useState } from "react"
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material"
import ViewListIcon from "@mui/icons-material/ViewList" // Icono para Lotes
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered" // Icono para Series
import SearchIcon from "@mui/icons-material/Search" // Icono para Stock
import Swal from "sweetalert2"
import { showWarning } from "../../../../api"

// Importación de Modales Transaccionales
import ModalLotesIngreso from "./ModalLotesIngreso"
import ModalLotesEgreso from "./ModalLotesEgreso"
import ModalSeriesIngreso from "./ModalSeriesIngreso"
import ModalSeriesEgreso from "./ModalSeriesEgreso"
import ModalStockAgrupado from "./ModalStockAgrupado" // Modal de consulta de stock que crearemos a continuación

const DetalleArticulosInvGrid = ({ detalle, setFormData, tratipo, bodcodigo, sgastatus, tsolInEg }) => {
  const [activeRowIndex, setActiveRowIndex] = useState(null)
  const [modalType, setModalType] = useState("") // 'LOTES' o 'SERIES'

  // Estado para el Modal de Consulta de Stock
  const [stockModalOpen, setStockModalOpen] = useState(false)
  const [activeStockArticulo, setActiveStockArticulo] = useState(null)

  // Bandera para asegurar que la pregunta de "Replicar Bodega" se haga solo una vez
  const [hasPromptedBodega, setHasPromptedBodega] = useState(false)

  // ==========================================================================
  // MANEJADOR DE CAMBIO DE BODEGA Y LÓGICA "REPLICAR A TODOS"
  // ==========================================================================
  const handleBodegaChange = async (index, value) => {
    const val = value.toUpperCase().trim()

    // 1. Actualizamos la bodega de la fila actual inmediatamente
    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[index].bodcodigo = val
      return { ...prev, detalle: nuevoDetalle }
    })

    // 2. Si es la primera vez que asignan una bodega y el valor no está vacío, lanzamos la pregunta interactiva
    if (!hasPromptedBodega && val !== "") {
      // Verificamos si hay otras filas pendientes que podrían necesitar esta bodega
      const hayOtrasPendientes = detalle.some((row, i) => i !== index && row.cant_pendiente > 0)

      if (hayOtrasPendientes) {
        setHasPromptedBodega(true) // Evitamos que pregunte de nuevo

        const confirm = await Swal.fire({
          title: "¿Replicar Bodega?",
          html: `¿Desea asignar la bodega <b>${val}</b> a todos los demás artículos pendientes de la solicitud?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, aplicar a todos",
          cancelButtonText: "No, solo a esta línea",
          confirmButtonColor: "#196C87",
          cancelButtonColor: "#757575",
          reverseButtons: true,
        })

        if (confirm.isConfirmed) {
          setFormData((prev) => {
            const nuevoDetalle = prev.detalle.map((row) => {
              // Solo sobreescribimos las líneas que aún tienen saldo pendiente
              if (row.cant_pendiente > 0) {
                return { ...row, bodcodigo: val }
              }
              return row
            })
            return { ...prev, detalle: nuevoDetalle }
          })
        }
      }
    }
  }

  // ==========================================================================
  // MANEJADOR DE CANTIDADES
  // ==========================================================================
  const handleCantidadChange = (index, value) => {
    let val = parseFloat(value) || 0

    if (val < 0) val = 0

    if (val > detalle[index].cant_pendiente) {
      showWarning(
        `La cantidad a afectar no puede ser mayor a la cantidad pendiente (${detalle[index].cant_pendiente}).`,
      )
      val = detalle[index].cant_pendiente
    }

    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      if (nuevoDetalle[index].cant_afectar !== val) {
        nuevoDetalle[index].lotes = []
        nuevoDetalle[index].series = []
      }
      nuevoDetalle[index].cant_afectar = val
      return { ...prev, detalle: nuevoDetalle }
    })
  }

  // ==========================================================================
  // MANEJADORES DE APERTURA DE MODALES
  // ==========================================================================
  const handleOpenModal = (index, type) => {
    const row = detalle[index]

    if (!row.bodcodigo || row.bodcodigo.trim() === "") {
      return showWarning(`Primero asigne una Bodega al artículo ${row.artcodigo}.`)
    }

    if (row.cant_afectar <= 0) {
      return showWarning(
        `Primero digite la 'Cantidad a Afectar' en el artículo ${row.artcodigo} antes de asignar ${type}.`,
      )
    }

    setActiveRowIndex(index)
    setModalType(type)
  }

  const handleCloseModal = () => {
    setActiveRowIndex(null)
    setModalType("")
  }

  const handleOpenStock = (row) => {
    setActiveStockArticulo(row)
    setStockModalOpen(true)
  }

  // ==========================================================================
  // GUARDADO DE LOTES Y SERIES
  // ==========================================================================
  const handleSaveLotes = (lotes) => {
    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[activeRowIndex].lotes = lotes
      return { ...prev, detalle: nuevoDetalle }
    })
    handleCloseModal()
  }

  const handleSaveSeries = (series) => {
    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[activeRowIndex].series = series
      return { ...prev, detalle: nuevoDetalle }
    })
    handleCloseModal()
  }

  const currentRow = activeRowIndex !== null ? detalle[activeRowIndex] : null

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f5f7fa" }}>
            <TableRow>
              <TableCell width="3%">#</TableCell>
              <TableCell width="25%">Artículo (Código y Descripción)</TableCell>
              <TableCell width="12%">Bodega</TableCell>
              <TableCell width="10%" align="center">
                Cant. Solicitada
              </TableCell>
              <TableCell width="10%" align="center">
                Cant. Procesada
              </TableCell>
              <TableCell width="10%" align="center">
                Cant. Pendiente
              </TableCell>
              <TableCell width="15%" align="center">
                Cant. a Afectar
              </TableCell>
              <TableCell width="15%" align="center">
                Opciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detalle.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No hay artículos en el detalle de la solicitud.
                </TableCell>
              </TableRow>
            ) : (
              detalle.map((row, index) => {
                const procesada = row.sgacanrec + row.sgacanegr

                const lotesCompletos =
                  row.lotes &&
                  row.lotes.reduce((acc, curr) => acc + parseFloat(curr.cantidad || 0), 0) ===
                    parseFloat(row.cant_afectar)

                const seriesCompletas = row.series && row.series.length === parseFloat(row.cant_afectar)

                return (
                  <TableRow key={index} sx={{ backgroundColor: row.cant_pendiente === 0 ? "#f0f0f0" : "inherit" }}>
                    <TableCell>{index + 1}</TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {row.artcodigo}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {row.artdescri}
                          </Typography>
                        </Box>
                        {/* BOTÓN DE CONSULTA DE STOCK */}
                        <Tooltip title="Consultar Stock de este Artículo">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenStock(row)}
                            sx={{ ml: 1, color: "#196C87" }}
                          >
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    {/* NUEVA COLUMNA: BODEGA POR FILA */}
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Cód."
                        value={row.bodcodigo || ""}
                        onChange={(e) => handleBodegaChange(index, e.target.value)}
                        disabled={row.cant_pendiente === 0}
                        inputProps={{ maxLength: 3, style: { textTransform: "uppercase" } }}
                        fullWidth
                      />
                    </TableCell>

                    <TableCell align="center">{row.sgacansol}</TableCell>

                    <TableCell align="center">
                      <Typography variant="body2" color="secondary" fontWeight="bold">
                        {procesada}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        {row.cant_pendiente}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <TextField
                        type="number"
                        variant="outlined"
                        size="small"
                        inputProps={{ min: 0, max: row.cant_pendiente, step: "0.01" }}
                        value={row.cant_afectar === 0 ? "" : row.cant_afectar}
                        onChange={(e) => handleCantidadChange(index, e.target.value)}
                        disabled={row.cant_pendiente === 0}
                        fullWidth
                      />
                    </TableCell>

                    <TableCell align="center">
                      {row.artlote !== 0 && (
                        <Tooltip title="Asignar Lotes">
                          <span>
                            <IconButton
                              onClick={() => handleOpenModal(index, "LOTES")}
                              disabled={row.cant_pendiente === 0 || parseFloat(row.cant_afectar) <= 0}
                              sx={{
                                color: parseFloat(row.cant_afectar) > 0 && !lotesCompletos ? "#d32f2f" : "#2E7D32",
                              }}
                            >
                              <ViewListIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      {row.artserie !== 0 && (
                        <Tooltip title="Asignar Series">
                          <span>
                            <IconButton
                              onClick={() => handleOpenModal(index, "SERIES")}
                              disabled={row.cant_pendiente === 0 || parseFloat(row.cant_afectar) <= 0}
                              sx={{
                                color: parseFloat(row.cant_afectar) > 0 && !seriesCompletas ? "#d32f2f" : "#2E7D32",
                              }}
                            >
                              <FormatListNumberedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* =========================================================
          RENDERIZADO CONDICIONAL DE MODALES (INGRESO / EGRESO / STOCK)
      ========================================================= */}

      {modalType === "LOTES" && tratipo === "INGRESO" && (
        <ModalLotesIngreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveLotes}
          articulo={currentRow}
          bodcodigo={currentRow?.bodcodigo} // Usamos la bodega de la fila, no la general
        />
      )}

      {modalType === "LOTES" && tratipo === "EGRESO" && (
        <ModalLotesEgreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveLotes}
          articulo={currentRow}
          bodcodigo={currentRow?.bodcodigo}
        />
      )}

      {modalType === "SERIES" && tratipo === "INGRESO" && (
        <ModalSeriesIngreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveSeries}
          articulo={currentRow}
          bodcodigo={currentRow?.bodcodigo}
        />
      )}

      {modalType === "SERIES" && tratipo === "EGRESO" && (
        <ModalSeriesEgreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveSeries}
          articulo={currentRow}
          bodcodigo={currentRow?.bodcodigo}
        />
      )}

      {/* Modal de Consulta de Stock Transversal */}
      {stockModalOpen && activeStockArticulo && (
        <ModalStockAgrupado
          open={stockModalOpen}
          onClose={() => {
            setStockModalOpen(false)
            setActiveStockArticulo(null)
          }}
          articulo={activeStockArticulo}
        />
      )}
    </Box>
  )
}

export default DetalleArticulosInvGrid
